// Anwendungszustand + Live-Verbindung.
//
// Ein winziger Store mit Abonnenten statt verstreuter Modul-Variablen: die
// Screens hören auf Änderungen, statt sich gegenseitig neu zu zeichnen.
import type {
  PlanEntryView,
  ProductView,
  RecipeView,
  ShoppingItem,
  ShoppingListResponse,
  UserView,
} from '@speis/shared';
import { CACHE_KEYS, cacheGet, cacheSet } from './idb.js';

export interface ExpiringItem {
  batchId: number;
  productId: number;
  name: string;
  qty: number;
  bestBefore: string;
  daysLeft: number;
  imageUrl: string | null;
  pricePaid: number | null;
}

export interface AppState {
  me: UserView | null;
  shopping: ShoppingItem[];
  estimatedTotal: number | null;
  products: ProductView[];
  recipes: RecipeView[];
  plan: PlanEntryView[];
  expiring: ExpiringItem[];
  online: boolean;
  pendingOps: number;
  screen: ScreenName;
}

export type ScreenName = 'shopping' | 'pantry' | 'plan' | 'recipes' | 'food';

export const state: AppState = {
  me: null,
  shopping: [],
  estimatedTotal: null,
  products: [],
  recipes: [],
  plan: [],
  expiring: [],
  online: navigator.onLine,
  pendingOps: 0,
  screen: 'shopping',
};

type Key = keyof AppState;
type Listener = (changed: Set<Key>) => void;

const listeners = new Set<Listener>();

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Zustand ändern und alle Abonnenten mit den betroffenen Feldern wecken. */
export function setState(patch: Partial<AppState>): void {
  const changed = new Set<Key>();
  for (const [key, value] of Object.entries(patch) as [Key, never][]) {
    if (state[key] === value) continue;
    state[key] = value;
    changed.add(key);
  }
  if (!changed.size) return;
  for (const fn of listeners) fn(changed);
}

/** Antwort der Einkaufsliste übernehmen und lokal sichern. */
export function applyShopping(response: ShoppingListResponse): void {
  setState({ shopping: response.items, estimatedTotal: response.estimatedTotal });
  void cacheSet(CACHE_KEYS.shopping, response);
}

export function applyProducts(products: ProductView[]): void {
  setState({ products });
  void cacheSet(CACHE_KEYS.products, products);
}

/**
 * Einzelnes Produkt einsetzen oder ersetzen.
 *
 * Wichtig: Nach einem Schreibvorgang kommt der neue Stand oft ZWEIMAL – einmal
 * als Antwort auf die eigene Anfrage und einmal über den Live-Stream. Wer hier
 * blind anhängt, hat das Produkt doppelt in der Liste.
 */
export function upsertProduct(product: ProductView): void {
  const index = state.products.findIndex((p) => p.id === product.id);
  const next =
    index === -1
      ? [...state.products, product]
      : state.products.map((p) => (p.id === product.id ? product : p));
  applyProducts(next);
}

export function removeProduct(id: number): void {
  applyProducts(state.products.filter((p) => p.id !== id));
}

/** Beim Start: zuletzt gesehener Stand, bevor das Netz antwortet. */
export async function hydrateFromCache(): Promise<void> {
  const [shopping, products, expiring] = await Promise.all([
    cacheGet<ShoppingListResponse>(CACHE_KEYS.shopping),
    cacheGet<ProductView[]>(CACHE_KEYS.products),
    cacheGet<ExpiringItem[]>(CACHE_KEYS.expiring),
  ]);
  setState({
    ...(shopping ? { shopping: shopping.items, estimatedTotal: shopping.estimatedTotal } : {}),
    ...(products ? { products } : {}),
    ...(expiring ? { expiring } : {}),
  });
}

export function productById(id: number | null | undefined): ProductView | undefined {
  if (id == null) return undefined;
  return state.products.find((p) => p.id === id);
}
